// â”€â”€â”€ Contact (canonical person registry) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Account Portal's canonical person registry. One doc per person â€” employee,
// external worker, vendor, or guest â€” held in `contacts/{contactId}`.
//
// Design intent (see docs/CONTACTS_IMPLEMENTATION_PLAN.md and memory file
// `contacts_directory_decision.md`):
//
//   1. Identity + HRIS lives here. `users/{uid}` is a thin authorization
//      record, linked by `contactId` FK. Most ~280 employees stay contact-only
//      and receive magic-links without ever having a Firebase Auth account.
//
//   2. Adaptive-not-rigid: consumer apps (EPR, OKR, KPI, Leadership Portal)
//      may create contacts on the fly via @rascal/contacts-sdk. Account Portal
//      curates after the fact via the "Needs Review" queue.
//
//   3. Both `email` and `phone` are login identifiers (D9). Email is always
//      required as the unique primary key; phone is required when email is
//      synthetic (`*.local`) or when WhatsApp dispatch is desired.
//
// Invariant: every `users/{uid}` has `contactId` â†’ an existing Contact.
// Not every Contact has a user.

import type {
  UserType,
  EmploymentType,
  GradeId,
} from './index';

// â”€â”€â”€ Status â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// `active`   = contact participates in dispatch / login / picker queries.
// `inactive` = soft-deleted leaver. Historical FKs (reviews, KRs, cascade
//              tasks) stay valid; HR audit can still query inactive contacts;
//              login / OTP issuance rejected.

export type ContactStatus = 'active' | 'inactive';

export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  active:   'Active',
  inactive: 'Inactive',
};

// â”€â”€â”€ Source â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Where this contact first entered the directory. Surfaces in the "Needs
// Review" queue so HR sees context ("12 from EPR adhoc this week, 3 from OKR
// cascade"). Extensible â€” new consumer apps add prefixed values.

export type ContactSource =
  | 'manual'              // admin created via Account Portal UI
  | 'csv-import'          // bulk Account Portal CSV importer
  | 'm365-sync'           // Microsoft 365 nightly sync (Phase 13)
  | 'okr-cascade-auto'    // OKR cascade picker added on the fly
  | 'epr-adhoc'           // EPR ad-hoc review form added on the fly
  | 'epr-cycle-import'    // EPR cycle setup CSV / picker added
  | 'kpi-submit'          // KPI module submission form added
  | 'migration-2026-05'   // one-shot migration from legacy users/ (Phase 5)
  | 'self-signup';        // future user-initiated registration

// â”€â”€â”€ Delivery preference â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Hint to the HR-initiated dispatcher (EPR cycle invite, OKR cascade send).
// Login OTP is ALWAYS SMS regardless of this preference â€” preference governs
// outbound HR dispatch only.

export type DeliveryPreference =
  | 'email'
  | 'whatsapp'
  | 'auto';     // WhatsApp when phone present & valid, else email

export const DELIVERY_PREFERENCE_LABELS: Record<DeliveryPreference, string> = {
  email:    'Email',
  whatsapp: 'WhatsApp',
  auto:     'Auto (WhatsApp if available, else Email)',
};

// â”€â”€â”€ Contact interface â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface Contact {
  id: string;

  // â”€â”€â”€ Identity â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  /** Lowercased, unique. May be synthetic (`{employee_id}@field.rascal.local`)
   *  for field staff without a real email â€” in that case `phone` is required
   *  and `delivery_preference` should be `'whatsapp'`. */
  email: string;
  /** E.164 normalized (Indonesia: `628xxxxxxxxx`). Unique when present.
   *  Required if `email` is synthetic OR if `delivery_preference === 'whatsapp'`. */
  phone?: string;
  /** Free-text human name (e.g. "Andi Wijaya"). */
  displayName: string;
  status: ContactStatus;

  // â”€â”€â”€ Employment classification â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  user_type?: UserType;
  /** Derived from email domain (Â§5 of CONTACTS_DIRECTORY_ALGORITHM). Whitelist:
   *  `rascalrepublic.com`, `samaralombok.com`, `rinjanibay.com`. NOTE: this is
   *  a domain signal, NOT a kepegawaian signal â€” a field-staff `employee` with
   *  `@gmail.com` will have `isExternal: true` AND `user_type: 'employee'`. */
  isExternal?: boolean;
  employee_id?: string;
  employment_type?: EmploymentType;
  department?: string;
  jobTitle?: string;
  grade?: GradeId | string;

  // â”€â”€â”€ Org structure (D15: lives on contact, not users/) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Applies to non-login people too â€” cascade tasks + EPR line-manager
  // attribution need reportingTo for guest reviewers / contact-only employees.
  unitId?: string;
  unitName?: string;
  /** FK to another contactId. */
  directManagerId?: string;
  isLeader?: boolean;

  // â”€â”€â”€ Extended HRIS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  startDate?: string;            // ISO date string
  site?: string;
  nationality?: string;
  emergencyContact?: string;
  bio?: string;
  photoURL?: string;

  // â”€â”€â”€ Delivery / contact channels â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  delivery_preference?: DeliveryPreference;
  /** Set to `true` once the contact has successfully completed a phone OTP
   *  login at least once. Distinct from `phoneCanReceiveSms` (capability vs
   *  history). */
  phoneVerified?: boolean;
  /** `false` for virtual numbers / landlines that fail SMS delivery. Forces
   *  the login UI to suppress the "Login with phone" option for this contact. */
  phoneCanReceiveSms?: boolean;

  // â”€â”€â”€ Link to authorization layer (denormalized) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // `users/{uid}` is the source of truth for login state; these fields are
  // cached on contact for fast picker queries and badge rendering. Kept in
  // sync by the `hasAppAccount` flag-sync Cloud Function (Phase 7).
  hasAppAccount: boolean;
  appUserUid?: string;

  // ─── App membership flags (RR-Connect directory) ───────────────────────────
  // Which Rascal apps consume / reference this contact. Set TRUE by the app
  // itself (or by an admin) when the contact first appears in that app's
  // workflow. EPR autofills name/title/dept from contact when usedBy.epr=true;
  // OKR Tracker cascade picker filters by usedBy.okr; etc.
  //
  // Distinct from `hasAppAccount` (which means "has Firebase Auth login").
  // A contact can have usedBy.okr=true (appears in OKR cascade) WITHOUT
  // hasAppAccount=true (no login, only magic-link). Or vice versa.
  usedBy?: {
    okr?:    boolean;
    epr?:    boolean;
    portal?: boolean;  // Account Portal (i.e. shown in People page)
    kpi?:    boolean;
  };

  // â”€â”€â”€ Soft-merge pointer (Phase 3) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // When admin merges duplicates, the LOSER contact gets `status='inactive'`
  // + this field pointing to the survivor. Actual FK reassignment in consumer
  // apps (cascadeTasks, reviews, KRs) deferred to Phase 11+. Until then, apps
  // can follow the pointer to resolve to the canonical contact.
  mergedIntoContactId?: string;

  // â”€â”€â”€ Provenance + curation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  source: ContactSource;
  /** Auth uid of the admin/dispatcher who triggered creation. Used by Firestore
   *  rules to enforce `firstSeenBy == request.auth.uid` on client-side creates.
   *  For server-side automated creates (M365 sync, system) where admin SDK
   *  bypasses rules, use sentinel like `'system:m365-sync'`. */
  firstSeenBy: string;
  /** Firestore Timestamp. */
  firstSeenAt: any;
  /** Set `true` for auto-created contacts. HR clears via the "Needs Review"
   *  queue (Phase 2 UI). */
  needsReview: boolean;
  /** Field names admin has manually overridden. M365 sync (Phase 13) MUST
   *  NOT overwrite any field in this list â€” admin edits win. */
  manualEdits: string[];

  // â”€â”€â”€ M365 sync (Phase 13) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  /** Azure AD `objectId` â€” stable id for sync merge. */
  m365ObjectId?: string;
  /** Firestore Timestamp. Last time M365 sync touched this contact. */
  lastSyncedAt?: any;

  // â”€â”€â”€ Timestamps â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  /** Firestore Timestamp. */
  createdAt: any;
  /** Firestore Timestamp. */
  updatedAt: any;
}
